import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Pencil, Check, X } from 'lucide-react'
import { manualEdit, type ManualEditTargetType } from '../api'

/**
 * EditableText — pencil-icon-driven inline editor for AI-generated fields.
 *
 * UX (after researching common patterns: GitHub PR titles, Notion blocks,
 * Linear issue fields, Asana task names):
 *
 *   - Display mode is the natural value (just text). NEVER click-to-edit on
 *     the value itself — that's the worst pattern: users can't select text
 *     to copy, can't read carefully without accidentally entering edit
 *     mode, and screen readers misinterpret the affordance.
 *
 *   - A small dimmed pencil icon appears top-right on hover (the parent
 *     container needs `group` for this — we use Tailwind's `group-hover:`
 *     pattern). Click pencil to enter edit mode. Always visible on touch
 *     devices via a fallback (we keep it always visible at smaller-than-
 *     hover-supporting widths via the `always` prop).
 *
 *   - Edit mode: input or textarea, Save (✓) and Cancel (✗) buttons, plus
 *     an optional small "reason" input. Enter saves (single-line) or
 *     Cmd/Ctrl+Enter saves (multi-line). Esc cancels.
 *
 *   - On save: POST /api/manual-edit/, then refetch the parent's queries.
 *     On error: show inline message; field stays in edit mode so user can
 *     fix and retry.
 *
 *   - When the value has been manually edited, show a tiny "edited" pill
 *     after the text on hover (but not in edit mode), with a tooltip of
 *     who/when. Driven by props.lastEdit.
 */

type Props = {
  value: string
  /** Backend target — used for the POST. */
  target: { type: ManualEditTargetType; id: number; field: string }
  /** Render layout. 'inline' wraps in a span (good for headings),
   *  'block' wraps in a div (good for summaries/notes). */
  mode?: 'inline' | 'block'
  /** When true, render value as read-only without the pencil icon
   *  (useful when the surrounding component has no edit permission). */
  readOnly?: boolean
  /** Always show the pencil icon (don't gate on hover). Useful for
   *  touch / smaller screens. */
  alwaysShowPencil?: boolean
  /** Use a textarea (multi-line) instead of single-line input. */
  multiline?: boolean
  /** Maximum length for the edited value (validated client-side AND
   *  server-side). */
  maxLength?: number
  /** Wrapper className for the display value (so parents can keep their
   *  existing typography). */
  className?: string
  /** When the user saves successfully, the parent's invalidate keys.
   *  We invalidate every match to refresh the surrounding view. */
  invalidateKeys?: readonly (string | number | object)[][]
  /** Optional placeholder shown when the value is empty. */
  placeholder?: string
  /** Optional last-edit info (used to render an "edited" indicator). */
  lastEdit?: { user_username: string | null; created_at: string } | null
  /** When true (and not editing), clicking the value text copies it to
   *  the clipboard. The display gets a `cursor: copy` affordance and a
   *  brief green flash on copy. The pencil-icon's onClick uses
   *  stopPropagation so opening the editor never triggers a copy. */
  copyOnClick?: boolean
}

export default function EditableText({
  value,
  target,
  mode = 'block',
  readOnly = false,
  alwaysShowPencil = false,
  multiline = false,
  maxLength = 5000,
  className = '',
  invalidateKeys = [],
  placeholder = '',
  lastEdit = null,
  copyOnClick = false,
}: Props) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const [reason, setReason] = useState('')
  const [showReason, setShowReason] = useState(false)
  const [copiedFlash, setCopiedFlash] = useState(false)
  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null)

  // Click-to-copy on the value text (when copyOnClick is on). Pencil
  // has its own stopPropagation, so opening the editor never copies.
  // Editing the field disables the handler entirely (the editor renders
  // its own input element below).
  function handleCopy(e: React.MouseEvent) {
    if (!copyOnClick || editing || !value) return
    e.stopPropagation()
    try {
      navigator.clipboard.writeText(value)
      setCopiedFlash(true)
      window.setTimeout(() => setCopiedFlash(false), 1200)
    } catch { /* clipboard rejected (insecure context / permissions) — silent */ }
  }
  // Per Ken 2026-05-25: drop the cursor-copy affordance (the "green
  // plus-sign" cursor when hovering a click-to-copy zone). The click
  // handler, tooltip, and copy-flash all stay intact — just no longer
  // telegraph copyability via cursor change.
  const copyClass = copyOnClick && !editing && copiedFlash ? 'me-flash-copy' : ''
  const copyTitle = copyOnClick && !editing ? 'Click to copy' : undefined

  useEffect(() => { if (editing) setDraft(value) }, [editing, value])
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      // Place caret at end on enter.
      const v = inputRef.current.value
      try { inputRef.current.setSelectionRange(v.length, v.length) } catch { /* ignore */ }
    }
  }, [editing])

  const save = useMutation({
    mutationFn: () => manualEdit({
      target_type: target.type, target_id: target.id, field: target.field,
      value: draft.trim(), reason: reason.trim() || undefined,
    }),
    onSuccess: () => {
      setEditing(false)
      setReason('')
      setShowReason(false)
      for (const k of invalidateKeys) qc.invalidateQueries({ queryKey: k })
    },
  })

  function cancel() {
    setEditing(false)
    setDraft(value)
    setReason('')
    setShowReason(false)
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      e.preventDefault(); cancel()
      return
    }
    if (e.key === 'Enter') {
      // Single-line: Enter saves. Multi-line: Cmd/Ctrl+Enter saves.
      const shouldSave = !multiline || (e.metaKey || e.ctrlKey)
      if (shouldSave) {
        e.preventDefault()
        if (draft.trim() && draft.trim() !== value) save.mutate()
        else cancel()
      }
    }
  }

  if (readOnly) {
    return mode === 'inline'
      ? <span className={className}>{value || placeholder}</span>
      : <div className={className}>{value || placeholder}</div>
  }

  if (editing) {
    // Edit mode is ALWAYS rendered as a block-level container, regardless
    // of `mode`. Two reasons:
    //   1. Inline-mode parents (e.g. inside a button or flex row) would
    //      otherwise stretch the editor to fill remaining space and shove
    //      siblings around.
    //   2. textarea/input nested inside <button> is invalid HTML and
    //      causes spacebar to bubble to the parent button → accidental
    //      collapse/expand. Block-level + stopPropagation is the cure.
    return (
      <div
        className="space-y-1.5"
        // Stop propagation aggressively so EditableText nested inside
        // a clickable parent never triggers that parent.
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        onKeyUp={(e) => e.stopPropagation()}
        onKeyPress={(e) => e.stopPropagation()}
      >
        {multiline ? (
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            rows={Math.max(3, Math.min(14, draft.split('\n').length + 2))}
            maxLength={maxLength}
            className="me-input w-full text-[13px]"
          />
        ) : (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            maxLength={maxLength}
            className="me-input w-full text-[13px]"
          />
        )}
        {showReason ? (
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Optional: why are you changing this?"
            maxLength={240}
            className="me-input w-full !text-[11.5px] !py-1"
          />
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); setShowReason(true) }}
            className="text-[10.5px] me-text-mute underline"
            type="button"
          >
            + add reason
          </button>
        )}
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation()
              if (draft.trim() && draft.trim() !== value) save.mutate()
            }}
            disabled={save.isPending || !draft.trim() || draft.trim() === value}
            className="me-btn me-btn-primary !text-[11px] !py-1 inline-flex items-center gap-1"
            title={multiline ? 'Save (⌘/Ctrl+Enter)' : 'Save (Enter)'}
            type="button"
          >
            <Check size={11} />
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); cancel() }}
            className="me-btn me-btn-outline !text-[11px] !py-1 inline-flex items-center gap-1"
            title="Cancel (Esc)"
            type="button"
          >
            <X size={11} /> Cancel
          </button>
          <span className="ml-2 text-[10px] me-text-mute italic">
            {multiline ? '⌘/Ctrl+Enter to save · Esc to cancel' : 'Enter to save · Esc to cancel'}
          </span>
        </div>
        {save.error && (
          <div className="text-[11px] text-[color:var(--me-stale)]">{(save.error as Error).message}</div>
        )}
      </div>
    )
  }

  // Display mode. Two placement strategies:
  //
  // - mode='block' (multi-line text like summaries):  pencil is
  //   absolutely positioned at the TOP-RIGHT of the value box.
  //   The wrapper is a block <div> with position:relative; its width
  //   already matches the parent's flow width (block-level), so adding
  //   position:relative doesn't change layout. The value gets a small
  //   right-padding so trailing text doesn't sit under the pencil.
  //
  // - mode='inline' (titles / short labels):  pencil sits AFTER the
  //   value, glued to the last word via a non-breaking space + a
  //   white-space:nowrap span. This stops the pencil from wrapping to
  //   its own line when the text fills the row. We can't absolute-
  //   position the pencil for inline mode without breaking flex+truncate
  //   parents (the position:relative inline wrapper measured its
  //   intrinsic width into the flex-shrink calculation and collapsed
  //   sibling columns to zero — see narrative-group bug history).
  //
  // Both strategies keep the pencil dimly visible at all times
  // (opacity 0.32) so users can find it without guessing where to
  // hover; full opacity on direct hover.
  const baseOpacity = alwaysShowPencil ? 0.5 : 0.32
  const pencilButton = (
    <button
      onClick={(e) => { e.stopPropagation(); setEditing(true) }}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.stopPropagation()
        }
      }}
      title="Edit"
      type="button"
      aria-label="Edit"
      className="me-text-mute hover:me-text transition-opacity"
      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1' }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = String(baseOpacity) }}
      style={{ opacity: baseOpacity, padding: 0, lineHeight: 0 }}
    >
      <Pencil size={11} />
    </button>
  )

  if (mode === 'block') {
    return (
      <div className={`group relative ${className}`}>
        <div
          style={{ paddingRight: 18 }}
          className={copyClass}
          title={copyTitle}
          onClick={handleCopy}
        >
          {value || (placeholder ? <span className="me-text-mute italic">{placeholder}</span> : null)}
        </div>
        <div
          style={{ position: 'absolute', top: 0, right: 0, padding: 1 }}
          className="inline-flex items-center"
        >
          {pencilButton}
        </div>
        {lastEdit && (
          <span
            className="ml-1.5 align-baseline text-[9.5px] uppercase me-text-mute opacity-0 group-hover:opacity-100"
            title={`Last edited by ${lastEdit.user_username ?? 'unknown'} at ${new Date(lastEdit.created_at).toLocaleString()}`}
          >
            edited
          </span>
        )}
      </div>
    )
  }

  // inline mode
  return (
    <span className={`group ${className}`}>
      <span className={copyClass} title={copyTitle} onClick={handleCopy}>
        {value || (placeholder ? <span className="me-text-mute italic">{placeholder}</span> : null)}
      </span>
      {/* white-space:nowrap span keeps NBSP + pencil glued together,
       *  preventing the pencil from wrapping onto a line by itself. */}
      <span style={{ whiteSpace: 'nowrap' }}>
        {' '}
        <span
          className="inline-flex items-center"
          style={{ verticalAlign: 'text-top', marginLeft: 4 }}
        >
          {pencilButton}
        </span>
      </span>
      {lastEdit && (
        <span
          className="ml-1.5 align-baseline text-[9.5px] uppercase me-text-mute opacity-0 group-hover:opacity-100"
          title={`Last edited by ${lastEdit.user_username ?? 'unknown'} at ${new Date(lastEdit.created_at).toLocaleString()}`}
        >
          edited
        </span>
      )}
    </span>
  )
}
